<template>
  <CustomDatatable :rows="rows" :columns="columns">
    <template #footer>
      <div class="footer-text">* denotes corrected viscosity rate</div>
    </template>
  </CustomDatatable>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { injectStrict } from "@/utils/utils";
import {
  scenarioCalculationsKey,
  scenarioKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { PercentageUnits, Ratio } from "@/libs/units/percentage";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { convert } from "@/libs/units";
import { BarrelsPerDay } from "@/libs/units/flowRate";
import { LengthUnits } from "@/libs/units/length";
import { PoundsPerCubicFoot } from "@/libs/units/density";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { Scenario, Sizing } from "@/composables/forms/formTypes";
import CustomDatatable from "../primevueless/CustomDatatable.vue";

const props = defineProps<{
  sizing?: Sizing | null | undefined;
  scenario?: Scenario | null;
  scenarioCalculations?: ScenarioCalculations;
  isReport?: boolean;
}>();

const _scenario = props.scenario || injectStrict(scenarioKey);

const { descendingOrderPumps, pumpCalculations } = !props.isReport
  ? injectStrict(scenarioCalculationsKey)
  : {
      descendingOrderPumps: undefined,
      pumpCalculations: undefined
    };

const { getLiquidFlowUnit, getLargeLengthUnit, getDensityUnit } =
  useScenarioUnits(_scenario, props.sizing);

// The override keys used here will hopefully go away if we add unit controls
// specifically for the pump table/charts
const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.convert.STB/D"
);
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetDensityUnit = getDensityUnit(
  "Discharge Fluid Density.convert.lb/ft³"
);

const columns = computed(() => [
  { field: "title", header: `Pump` },
  { field: "ROR", header: `ROR (${liquidFlowRateUnitLabel.value})` },
  {
    field: "intakeQ",
    header: `Q @ INT (${liquidFlowRateUnitLabel.value})`
  },
  {
    field: "dischargeQ",
    header: `Q @ DIS (${liquidFlowRateUnitLabel.value})`
  },
  { field: "intakeGas", header: `Gas @ INT` },
  { field: "dischargeGas", header: `Gas @ DIS` },
  {
    field: "freeAllowedGas",
    header: `Free Allowed Gas`
  },
  { field: "lift", header: `Lift (${targetLargeLengthUnit.value.shortLabel})` },
  {
    field: "intakeDensity",
    header: `Density @ INT (${targetDensityUnit.value.shortLabel})`
  },
  {
    field: "dischargeDensity",
    header: `Density @ DIS (${targetDensityUnit.value.shortLabel})`
  },
  {
    field: "shaftLoad",
    header: `% Shaft Load`
  }
]);
const descendingPumps = props.isReport
  ? props.scenarioCalculations?.descendingOrderPumps
  : descendingOrderPumps;

const liquidFlowRateUnitLabel = computed(() => {
  return (
    targetLiquidFlowUnit.value.inSituLabel ||
    targetLiquidFlowUnit.value.shortLabel ||
    targetLiquidFlowUnit.value.label
  );
});

const shaftLoad = computed(() =>
  descendingPumps?.value.map(xrefPump => {
    const useHighStrengthShaft = !!xrefPump.use_high_strength_shaft;

    const calcs = props.isReport
      ? (props.scenarioCalculations?.pumpCalculations.individual.value[
          xrefPump.id
        ] as unknown as ScenarioCalculations["pumpCalculations"]["individual"]["value"][number])
      : (pumpCalculations?.individual.value[
          xrefPump.id
        ] as unknown as ScenarioCalculations["pumpCalculations"]["individual"]["value"][number]);

    return useHighStrengthShaft
      ? PercentageUnits.Ratio.toStandardUnit(calcs.highStrengthShaftRatingRatio)
      : PercentageUnits.Ratio.toStandardUnit(calcs.standardShaftRatingRatio);
  })
);

const rows = computed(() =>
  descendingPumps?.value.map((xrefPump, idx) => {
    const pump = xrefPump.r_pump;

    const calcs = props.isReport
      ? (props.scenarioCalculations?.pumpCalculations.individual.value[
          xrefPump.id
        ] as unknown as ScenarioCalculations["pumpCalculations"]["individual"]["value"][number])
      : (pumpCalculations?.individual.value[
          xrefPump.id
        ] as unknown as ScenarioCalculations["pumpCalculations"]["individual"]["value"][number]);

    const shaftLoadValue = shaftLoad.value?.[idx] ?? undefined;

    const isCorrected = xrefPump.rate_correction_ratio !== 1;

    return {
      title: isCorrected ? `* ${pump?.series_model}` : `${pump?.series_model}`,
      ROR: `${convert(
        calcs.rorMin,
        BarrelsPerDay,
        targetLiquidFlowUnit.value
      ).toFixed(0)} - ${convert(
        calcs.rorMax,
        BarrelsPerDay,
        targetLiquidFlowUnit.value
      ).toFixed(0)}`,
      intakeQ: `${convert(
        calcs.intakeStageCalculations?.intakeFluidPropertiesContext
          .flowRatesInSitu?.fluidFlowRateBBLD,
        BarrelsPerDay,
        targetLiquidFlowUnit.value
      ).toFixed(0)}`,
      dischargeQ: convert(
        calcs.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .flowRatesInSitu?.fluidFlowRateBBLD,
        BarrelsPerDay,
        targetLiquidFlowUnit.value
      ).toFixed(0),
      intakeGas: `${Ratio.toStandardUnit(calcs.gasAtIntakeRatio).toFixed(2)}%`,
      dischargeGas: `${Ratio.toStandardUnit(calcs.gasAtDischargeRatio).toFixed(
        2
      )}%`,
      freeAllowedGas: `${Ratio.toStandardUnit(calcs.gasLockRatio).toFixed(1)}%`,
      lift: convert(
        calcs.pumpGeneratedLiftHeadFt,
        LengthUnits.Feet,
        targetLargeLengthUnit.value
      ).toFixed(0),
      intakeDensity: convert(
        calcs.intakeStageCalculations?.intakeFluidPropertiesContext
          .fluidProperties.fluidDensityLBMFT3,
        PoundsPerCubicFoot,
        targetDensityUnit.value
      ).toFixed(2),
      dischargeDensity: convert(
        calcs.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties.fluidDensityLBMFT3,
        PoundsPerCubicFoot,
        targetDensityUnit.value
      ).toFixed(2),
      shaftLoad:
        shaftLoadValue !== undefined ? `${shaftLoadValue.toFixed(2)}%` : ""
    };
  })
);
</script>

<style lang="scss" scoped>
@import "@/assets/scss/theme-spyglass/components/typography.scss";
.pump-calc-table {
  width: 100%;
  box-shadow: var(--shadow-main);
  margin-bottom: 1em;
  :deep(.p-datatable-thead tr th) {
    font-size: 12px;
    padding: 0.25em;
  }
  :deep(.p-datatable-tbody tr) {
    td {
      padding: 0.25em;
      &:not(first-child) {
        @extend .body-2;
        font-family: var(--monospace-family);
      }

      &:first-child {
        @extend .heading-3;
        font-family: var(--font-family);
      }
    }
  }
}

.footer-text {
  font-size: x-small;
}
</style>
